import { readdirSync, readFileSync, statSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { requiredMcpWildcard } from '@augmented/core';

/**
 * ENG-5922 — runtime audit for subagent .md `tools:` allowlist
 * staleness vs the agent's actual `.mcp.json` `mcpServers` keys.
 *
 * Why this exists: ENG-5897 + ENG-5905 added the `augmented-worker`
 * sub-agent on the same dynamic-render path as the existing
 * `channel-message-handler` sub-agent. Don's empirical re-test
 * 2026-06-02 showed the rendered `tools:` allowlist on his host was
 * stale — only a subset of the actual `.mcp.json` keys. Causes:
 * (a) the direct-chat manager-worker write bypassed the render
 * chokepoint (fixed in this PR), and (b) the `agt provision` command
 * builds `.mcp.json` without integration data, producing a 1-server
 * file that pollutes the rendered .md (tracked separately as a
 * follow-up). The render-vs-.mcp.json drift had been hidden in
 * production for months because nobody empirically tested the
 * matching invariant.
 *
 * The runtime audit closes the gap by checking, for each managed
 * agent on the host, whether the rendered `tools:` allowlist in
 * `channel-message-handler.md` and `augmented-worker.md` is a
 * superset of the live `.mcp.json` `mcpServers` keys. Drift surfaces
 * as a finding the operator (and CI in `--strict`) can act on
 * without waiting for an agent to silently fail on a missing MCP
 * tool. Strictly read-only — no file mutation.
 */

export interface RenderDriftFinding {
  /** Agent code_name (the directory name under `~/.augmented/`). */
  agent: string;
  /** Which sub-agent .md is out of sync. */
  subagent: 'channel-message-handler' | 'augmented-worker';
  /** Whether the file we read lives in the provision/ canonical or project/ mirror. */
  scope: 'provision' | 'project';
  /** Server keys present in .mcp.json that the .md's tools allowlist DOESN'T cover. */
  missing: string[];
  /** Absolute path to the .md file with the stale allowlist. */
  file: string;
}

/**
 * The wildcard an allowlist MUST contain for a server's tools to bind.
 *
 * ENG-8181: this used to rewrite hyphens to underscores, mirroring the same
 * rewrite in the renderer it audits. Both were wrong in the same direction, so
 * the audit compared a wrong expectation against a wrong rendering and reported
 * the fleet clean while every hyphenated server (`direct-chat`,
 * `augmented-admin`, `composio_gmail-personal-mailbox`) was silently filtered
 * out of every sub-agent.
 *
 * Now delegated to `requiredMcpWildcard`, which is deliberately the VERBATIM
 * form only. Accepting the underscored spelling as sufficient would restore
 * exactly the blind spot this function exists to remove — an agent rendered
 * before the fix is genuinely broken, and the audit's job is to say so.
 */
function expectedWildcard(serverKey: string): string {
  return requiredMcpWildcard(serverKey);
}

/**
 * Extract the `tools:` allowlist tokens from a rendered sub-agent
 * .md file. Returns the list of `mcp__*` wildcards (NOT the
 * built-ins like `Bash`/`Read`/etc., which are out of scope here).
 * Returns `null` when the file has no `tools:` line at all (would be
 * the inheritance-omitted shape ENG-5897 originally shipped — kept
 * as a distinct return so the caller can decide).
 */
export function parseMcpWildcardsFromToolsLine(content: string): string[] | null {
  const toolsLine = content.split('\n').find((l) => l.startsWith('tools:'));
  if (!toolsLine) return null;
  const value = toolsLine.replace(/^tools:\s*/, '').trim();
  if (value.length === 0) return [];
  return value
    .split(/\s*,\s*/)
    .map((t) => t.trim())
    .filter((t) => t.startsWith('mcp__'));
}

/**
 * Read an agent's `.mcp.json` (provision-scope) and return the
 * top-level `mcpServers` keys, or `null` when the file is missing
 * or unreadable — the caller silently skips that agent in those
 * cases.
 */
function readMcpServerKeys(mcpJsonPath: string): string[] | null {
  try {
    const config = JSON.parse(readFileSync(mcpJsonPath, 'utf-8')) as {
      mcpServers?: Record<string, unknown>;
    };
    return Object.keys(config.mcpServers ?? {});
  } catch {
    return null;
  }
}

/**
 * Scan every managed-agent directory under `~/.augmented/` (one
 * directory per `code_name`) and report any sub-agent .md whose
 * `tools:` allowlist is missing a wildcard for a server present in
 * the agent's `.mcp.json`. Both the `provision/` canonical and the
 * `project/` mirror are checked — the mirror is what Claude Code
 * actually reads at session-spawn time, but a provision/ drift is
 * still a bug (the next sync will propagate it to project/).
 */
export function auditMcpRenderAllowlist(
  rootDir: string = join(homedir(), '.augmented'),
): RenderDriftFinding[] {
  const findings: RenderDriftFinding[] = [];

  let entries: string[];
  try {
    entries = readdirSync(rootDir);
  } catch {
    return findings; // Root missing — no managed agents on this host.
  }

  for (const entry of entries) {
    const agentDir = join(rootDir, entry);
    let s: ReturnType<typeof statSync>;
    try {
      s = statSync(agentDir);
    } catch {
      continue;
    }
    if (!s.isDirectory() || entry.startsWith('_') || entry.startsWith('.')) continue;

    // For each scope, audit both sub-agent files against the
    // canonical .mcp.json from THAT scope (provision vs project).
    // A provision/ drift indicates the render at agt-provision time
    // missed a server; a project/ drift indicates a subsequent
    // mutation didn't re-trigger the render.
    for (const scope of ['provision', 'project'] as const) {
      const mcpJsonPath = scope === 'provision'
        ? join(agentDir, 'provision', '.mcp.json')
        : join(agentDir, 'project', '.mcp.json');
      const keys = readMcpServerKeys(mcpJsonPath);
      if (keys === null || keys.length === 0) continue;
      const expected = new Set(keys.map(expectedWildcard));

      for (const subagent of ['channel-message-handler', 'augmented-worker'] as const) {
        const mdPath = scope === 'provision'
          ? join(agentDir, '.claude', 'agents', `${subagent}.md`)
          : join(agentDir, 'project', '.claude', 'agents', `${subagent}.md`);
        let content: string;
        try {
          content = readFileSync(mdPath, 'utf-8');
        } catch {
          continue; // .md not yet rendered (new agent or agt-pre-ENG-5905) — skip.
        }
        const declared = parseMcpWildcardsFromToolsLine(content);
        if (declared === null) continue; // no tools: line → inheritance mode (different shape).
        const declaredSet = new Set(declared);
        const missing = [...expected].filter((w) => !declaredSet.has(w));
        if (missing.length > 0) {
          findings.push({ agent: entry, subagent, scope, missing, file: mdPath });
        }
      }
    }
  }

  return findings;
}

/**
 * Single-line structured log entry matching the manager-worker
 * stderr convention. Callers (the CLI + the manager boot probe)
 * decide where to emit.
 */
export function formatRenderDriftFinding(f: RenderDriftFinding): string {
  return `[manager-worker] [mcp-render-drift] agent=${f.agent} subagent=${f.subagent} scope=${f.scope} missing=${JSON.stringify(f.missing)} file=${f.file}`;
}
